import React, { useMemo } from 'react';

import styled from 'styled-components';

import { FormattedMessage } from '@techstyle/react-intl';
import type {
  CartDto,
  CartItemDto,
  OrderLineItemDto,
} from '@techstyle/react-types';

import { MembershipActionLocation } from '../../constants/checkout';
import { BasicButton } from '../../styles/button';
import isNewPricingPromoBannersFlagOn from '../../utils/isNewPricingPromoBannersFlagOn';
import { useLDFlags } from '../../utils/LD/useLDFlags';
import { useBasketListPropsAdapter } from '../CartDrawer/CartDrawerBasketItemList';
import { useItemExceedingStockCheck } from '../CartDrawer/useItemExceedingStockCheck';
import { JoinVipAnnual } from '../JoinVipAnnual/JoinVipAnnual';
import {
  MembershipDetails,
  StyledText as MembershipStyledText,
} from '../JoinVipAnnual/shared';
import { Container as VipAnnualItem } from '../VipAnnualItem';

import {
  ShoppingBagBundleEligibleItemListRenderer,
  ShoppingBagGeneralItemListRenderer,
  ShoppingBagNewItemListRenderer,
} from './NewShoppingBagBasketItemListRendrers';
import { usePrepProducts } from './useShoppingBagProducts';

const Wrapper = styled.div<{ isTopItem?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ListWrapper = styled.div``;

const PreorderPerkCallout = styled.div`
  margin: 0 20px 0;
  padding: 15px 0;
  text-align: center;
`;

const PreorderPerkTitle = styled.div`
  color: ${props => props.theme.colors.promo};
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 4px;
`;

const PreorderPerkBody = styled.div`
  font-size: 12px;
`;

const PreorderPerkContinueButton = styled(BasicButton)<{
  index?: number;
  children: JSX.Element;
  fullWidth: boolean;
  secondary?: boolean;
  href: string;
}>`
  margin-top: ${props => props.theme.spacing.small}px;
  max-width: 250px;
`;

const JoinVipContainer = styled.div<{ hasMembership: boolean }>`
  ${({ hasMembership, theme }) =>
    !hasMembership && `background-color: ${theme.colors.lavender200};`}

  padding: ${props => props.theme.spacing.moderate}px ${props =>
    props.theme.spacing.small}px;

  ${MembershipStyledText} {
    color: ${({ theme }) => theme.colors.memberOffers};
  }
`;

const JoinVipInfo = styled.div`
  padding: ${({ theme }) => `0px ${theme.spacing.medium}px`}
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
`;

const Divider = styled.div`
  width: calc(100% - 32px);
  border-bottom: 1px solid ${props => props.theme.colors.stone};
  margin: 0 16px;
`;

type BasketItemListProps = {
  allowExclusiveCheckout?: boolean;
  lineDiscounts?: Array<OrderLineItemDto | CartItemDto>;
  className?: string;
  products: CartItemDto[];
  updateItemQuantity?: () => void;
  deleteBasketItem?: () => void;
  gwpProduct?: CartItemDto;
  isOrderItems?: boolean;
  removeMembership?: () => void;
  addMembership?: () => void;
  showExclusiveProductMessage?: boolean;
  showPrice?: boolean;
  showRemoveIcon?: boolean;
  showQuantitySelector?: boolean;
  showStock?: boolean;
  isBorderfreeCustomer: boolean;
  isVip: boolean;
  isInfluencer?: boolean;
  location?: string;
  membershipItem?: OrderLineItemDto;
  isTopItem?: boolean;
  showQuantity?: boolean;
  shouldShowVipInfo?: boolean;
  order?: CartDto;
  upsellProduct?: CartItemDto;
  pSource?: string;
  hasNonPreorderItems?: boolean;
  isEmpFeatureEnabled?: boolean;
  isMembershipSwitcherEnabled?: boolean;
  isOrderSummary?: boolean;
  membershipPrice?: number;
  upsellAssetToTrack?: {};
  showPriceLoading?: boolean;
};

const PreorderPerkCalloutRenderer = () => {
  const { isVip, isBorderfreeCustomer, hasNonPreorderItems } =
    useBasketListPropsAdapter();
  if (!isVip && !isBorderfreeCustomer && !hasNonPreorderItems) {
    return (
      <PreorderPerkCallout>
        <PreorderPerkTitle>
          <FormattedMessage
            id="global_checkout.preorder_perk_title"
            defaultMessage="Pre-order is an Xtra VIP Perk"
          />
        </PreorderPerkTitle>
        <PreorderPerkBody>
          <FormattedMessage
            id="global_checkout.preorder_perk_body"
            defaultMessage="Add an in-stock item to continue to checkout"
          />
        </PreorderPerkBody>
        <PreorderPerkContinueButton href="/products/new" fullWidth secondary>
          <FormattedMessage
            id="global_checkout.continue_shopping_cta"
            defaultMessage="Continue Shopping"
          />
        </PreorderPerkContinueButton>
      </PreorderPerkCallout>
    );
  }
  return null;
};

const useVipAnnualItemError = () => {
  const { products, membershipItem, isBorderfreeCustomer } =
    useBasketListPropsAdapter();

  return useMemo(() => {
    const hasOnlyMembershipItem =
      products.length === 1 &&
      membershipItem &&
      membershipItem.productId === products[0].productId;

    if (isBorderfreeCustomer && membershipItem) {
      return 'ERROR_BORDERFREE_MEMBERSHIP';
    } else if (hasOnlyMembershipItem) {
      return 'ERROR_ONLYMEMBERSHIP';
    } else {
      return null;
    }
  }, [isBorderfreeCustomer, membershipItem, products]);
};

function BasketItemList({
  className,
  removeMembership,
  addMembership = () => {},
  showRemoveIcon,
  isBorderfreeCustomer,
  isVip,
  isInfluencer = false,
  location,
  membershipItem,
  isTopItem,
  shouldShowVipInfo,
}: BasketItemListProps) {
  const {
    products: [newItems, qualifyingForBundle, generalProducts],
    effects: { hasExceedingStock },
  } = usePrepProducts();
  useItemExceedingStockCheck(hasExceedingStock);

  const {
    'new-product-pricing': shouldDisplayNewProductPricing,
    'experiment-new-pricing-promo-banners': newPricingPromoBannersFlag,
  } = useLDFlags();
  const displayNewProductPricing = isNewPricingPromoBannersFlagOn(
    newPricingPromoBannersFlag
  )
    ? newPricingPromoBannersFlag.newPricing
    : shouldDisplayNewProductPricing;
  const vipAnnualItemError = useVipAnnualItemError();

  const showMembershipRelatedContent =
    !isVip && !isInfluencer && !isBorderfreeCustomer;

  return (
    <Wrapper className={className} isTopItem={isTopItem}>
      <ListWrapper>
        <ShoppingBagNewItemListRenderer
          productsList={newItems}
          isLastInGroups={
            newItems.length > 0 &&
            qualifyingForBundle.length === 0 &&
            generalProducts.length === 0
          }
        />
        <ShoppingBagBundleEligibleItemListRenderer
          productsList={qualifyingForBundle}
          isLastInGroups={
            qualifyingForBundle.length > 0 && generalProducts.length === 0
          }
        />
        <ShoppingBagGeneralItemListRenderer
          productsList={generalProducts}
          isLastInGroups={generalProducts.length > 0}
        />

        <PreorderPerkCalloutRenderer />
        {showMembershipRelatedContent && (
          <>
            {membershipItem && <Divider />}

            <JoinVipContainer hasMembership={!!membershipItem}>
              {membershipItem ? (
                <>
                  <VipAnnualItem
                    item={membershipItem}
                    deleteBasketItem={removeMembership}
                    location={location}
                    showRemoveIcon={showRemoveIcon}
                    error={vipAnnualItemError}
                    isNewShoppingBagUX
                    title={
                      <FormattedMessage
                        id="global_checkout.minicart_membership_added"
                        defaultMessage="Membership Added"
                      />
                    }
                  />
                </>
              ) : (
                <>
                  <JoinVipAnnual
                    onClick={addMembership}
                    location={MembershipActionLocation.MINI_CART}
                    isNewProductPricingFlagSet={displayNewProductPricing}
                    isNewShoppingBagUX
                    title={
                      <FormattedMessage
                        id="global_checkout.minicart_add_membership"
                        defaultMessage="Add Membership"
                      />
                    }
                  />
                </>
              )}
              {shouldShowVipInfo && (
                <JoinVipInfo>
                  <MembershipDetails />
                </JoinVipInfo>
              )}
              <MembershipDetails showRemoveButton={false} />
            </JoinVipContainer>
          </>
        )}
      </ListWrapper>
    </Wrapper>
  );
}

export default BasketItemList;
